import { Button } from '@1trading/react/components/ui/button';
import { EditIcon, LockIcon } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingBackButton, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { useInstitutionOnboardingBasePath } from '@/features/institution/context/update';
import { useDetailsFlowControl } from '@/features/institution/hooks/useDetailsFlowControl';
import { useInstitutionDetailsLocked, useInstitutionDetailsV2Answers, useInstitutionDetailsVersion } from '@/features/institution/hooks/useInstitutionDetails';
import { useInstitutionOnboardingPrevDetailsRoute } from '@/features/institution/hooks/useInstitutionOnboardingPrevDetailsRoute';
import { getInstitutionReviewSectionSummary } from '@/features/institution/review/summary';
import { getV2OnboardingApplicableDetailsKeys, getV2OnboardingDetailsKeyRoute, INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import { useMainstreamCountries } from '@/hooks/queries/use-mainstream-countries';
import { INSTITUTION_DETAILS_VERSION } from '@/services/api/profile';

const InstitutionReview = () => {
  const { data: version } = useInstitutionDetailsVersion();
  const { data: institutionDetailsV2Answers } = useInstitutionDetailsV2Answers();
  const { fullyLockedKeys } = useInstitutionDetailsLocked();
  const { data: countries } = useMainstreamCountries();
  const { isLoading } = useDetailsFlowControl(INSTITUTION_DETAILS_TYPE.ONBOARDING, 'authorisation');
  const onboardingPath = useInstitutionOnboardingBasePath();
  const prevDetailsRoute = useInstitutionOnboardingPrevDetailsRoute('authorisation');
  const navigate = useNavigate();
  const { t } = useTranslation();

  const detailsKeys = useMemo(() => getV2OnboardingApplicableDetailsKeys(institutionDetailsV2Answers).filter((key) => key !== 'authorisation'), [institutionDetailsV2Answers]);

  const summaries = useMemo(() => {
    const getCountryName = (country: string) => countries?.[country]?.name ?? country;

    return Object.fromEntries(detailsKeys.map((key) => [key, getInstitutionReviewSectionSummary(key, institutionDetailsV2Answers ?? {}, { t, getCountryName })]));
  }, [countries, detailsKeys, institutionDetailsV2Answers, t]);

  const handlePrevStep = useCallback(() => {
    navigate(prevDetailsRoute, { replace: true });
  }, [navigate, prevDetailsRoute]);

  if (isLoading) return <LoadingCard />;

  if (version === INSTITUTION_DETAILS_VERSION.V1) return <Navigate to={`${onboardingPath}/authorisation`} replace />;

  return (
    <section className="relative">
      <PageHeading>
        <PageHeadingTitle>{t('institutionDetails.review.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.review.description')}</PageHeadingDescription>
      </PageHeading>
      <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={handlePrevStep} />
      <PageCard size="lg" bordered fixedFooter>
        <PageCardContent className="pt-8">
          <ul className="space-y-4">
            {detailsKeys.map((key) => (
              <li className="flex gap-4 lg:gap-16 items-center rounded-md p-4 relative bg-background" key={key}>
                <div className="flex-1 min-w-0 space-y-1">
                  <h3 className="font-medium">{t(`institutionDetails.review.sections.${key}`)}</h3>
                  {summaries[key] ? <p className="text-sm text-muted-foreground break-words">{summaries[key]}</p> : null}
                </div>
                {fullyLockedKeys.includes(key) ? (
                  <span className="-my-2 -mr-2 flex size-10 shrink-0 items-center justify-center text-muted-foreground" title={t('institutionDetails.review.locked')}>
                    <LockIcon aria-hidden className="size-4" />
                    <span className="sr-only">{t('institutionDetails.review.locked')}</span>
                  </span>
                ) : (
                  <Button
                    aria-label={`${t('fundamentals.edit')} ${t(`institutionDetails.review.sections.${key}`)}`}
                    className="-my-2 -mr-2 after:content-[''] after:absolute after:inset-0"
                    size="icon"
                    variant="link"
                    onClick={() => navigate(getV2OnboardingDetailsKeyRoute(onboardingPath, key), { replace: true })}
                  >
                    <EditIcon className="size-4" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </PageCardContent>
        <PageCardFooter>
          <Button className="w-full" size="lg" variant="primary" onClick={() => navigate(`${onboardingPath}/authorisation`, { replace: true })}>
            {t('institutionDetails.review.continue')}
          </Button>
        </PageCardFooter>
      </PageCard>
    </section>
  );
};
InstitutionReview.displayName = 'InstitutionReview';

export default InstitutionReview;
